import { useSearchParams } from "react-router-dom";
import { ScrollText } from "lucide-react";
import { PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { AdminTable, FilterTabs, ListFrame, MoreFooter, SearchBox } from "../components/jamora-admin/ListBits";
import useAdminList from "../hooks/useAdminList";
import { ACTION_LABELS, CATEGORY_LABELS, LEVEL_LABELS, LEVEL_TONES, SEVERITY_LABELS, SEVERITY_TONES } from "../lib/jamoraAdmin";
import { patchedSearch } from "../lib/searchParams";
import { formatDateTime } from "../lib/tickets";

const KIND_TABS = [["activity", "Activité"], ["audit", "Piste d'audit"], ["system", "Système"]];
const FILTERS = {
  activity: { param: "level", label: "Niveau", tabs: [["", "Tous"], ["INFO", "Information"], ["WARNING", "Avertissements"], ["ERROR", "Erreurs"], ["CRITICAL", "Critiques"]], placeholder: "Description, module, entité…" },
  audit: { param: "action", label: "Action", tabs: [["", "Toutes"], ["CREATE", "Créations"], ["UPDATE", "Modifications"], ["DELETE", "Suppressions"]], placeholder: "Objet, utilisateur…" },
  system: { param: "severity", label: "Sévérité", tabs: [["", "Toutes"], ["LOW", "Faible"], ["MEDIUM", "Moyenne"], ["HIGH", "Élevée"], ["CRITICAL", "Critique"]], placeholder: "Message, module, exception…" },
};
const DESCRIPTIONS = {
  activity: "Qui a fait quoi dans les pharmacies : action, module, entité. Les valeurs avant / après ne sont pas affichées.",
  audit: "Les modifications d'objets : seuls les noms des champs changés sont montrés, jamais leurs valeurs.",
  system: "Les incidents techniques de la plateforme : sévérité, module, type d'exception. Les piles d'appels ne sont pas affichées.",
};
const pharmacyCell = (r) => r.pharmacy?.name || "—";
const when = { id: "at", label: "Date", cell: (r) => formatDateTime(r.at) };

const COLUMNS = {
  activity: [
    when, { id: "pharmacy", label: "Pharmacie", primary: true, cell: pharmacyCell },
    { id: "user", label: "Utilisateur", cell: (r) => r.user || "—" },
    { id: "action", label: "Action", cell: (r) => ACTION_LABELS[r.action] || r.action },
    { id: "module", label: "Module", hide: "lg", cell: (r) => [r.module, r.entity_type].filter(Boolean).join(" · ") || "—" },
    { id: "description", label: "Description", hide: "lg", cell: (r) => r.description || "—" },
    { id: "level", label: "Niveau", cell: (r) => <Pill tone={LEVEL_TONES[r.level] || "slate"}>{LEVEL_LABELS[r.level] || r.level}</Pill> },
  ],
  audit: [
    when, { id: "pharmacy", label: "Pharmacie", primary: true, cell: pharmacyCell },
    { id: "user", label: "Utilisateur", cell: (r) => r.user || "—" },
    { id: "action", label: "Action", cell: (r) => ACTION_LABELS[r.action] || r.action },
    { id: "object", label: "Objet", cell: (r) => [r.object_type, r.object_id].filter((v) => v !== null && v !== undefined).join(" #") || "—" },
    { id: "fields", label: "Champs modifiés", hide: "lg", cell: (r) => (r.changed_fields.length ? r.changed_fields.join(", ") : "—") },
  ],
  system: [
    when, { id: "pharmacy", label: "Pharmacie", primary: true, cell: (r) => r.pharmacy?.name || "Plateforme" },
    { id: "severity", label: "Sévérité", cell: (r) => <Pill tone={SEVERITY_TONES[r.severity] || "slate"}>{SEVERITY_LABELS[r.severity] || r.severity}</Pill> },
    { id: "category", label: "Catégorie", hide: "lg", cell: (r) => CATEGORY_LABELS[r.category] || r.category },
    { id: "message", label: "Message", cell: (r) => r.message },
    { id: "where", label: "Module", hide: "lg", cell: (r) => [r.module, r.function, r.exception_type].filter(Boolean).join(" · ") || "—" },
    { id: "resolved", label: "Résolu", hide: "lg", cell: (r) => (r.resolved ? "Oui" : "Non") },
  ],
};

// The platform's journals (activity, audit trail, system), newest first. The HTTP request journal is deliberately not offered.
export default function JamoraAdminLogs() {
  const [params, setParams] = useSearchParams();
  const kind = FILTERS[params.get("tab")] ? params.get("tab") : "activity";
  const filter = FILTERS[kind];
  const level = params.get(filter.param) || "";
  const search = params.get("q") || "";
  const list = useAdminList(`/logs/${kind}`, { [filter.param]: level, search });
  const update = (patch) => setParams(patchedSearch(patch), { replace: true });

  return (
    <div className="space-y-4" data-testid="admin-logs">
      <PageHeader title="Journaux" description={DESCRIPTIONS[kind]} />
      <FilterTabs label="Journal" tabs={KIND_TABS} value={kind} onChange={(v) => setParams(v === "activity" ? {} : { tab: v }, { replace: true })} testid="logs-kind" />
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBox value={search} onChange={(v) => update({ q: v })} placeholder={filter.placeholder} testid="logs-search" />
        <FilterTabs label={filter.label} tabs={filter.tabs} value={level} onChange={(v) => update({ [filter.param]: v })} testid="logs-filter" />
      </div>
      <ListFrame list={list} empty={{ icon: ScrollText, title: "Aucune ligne", hint: level || search ? "Essayez un autre filtre." : "Ce journal est vide pour le moment.", testid: "logs-empty" }}>
        <AdminTable columns={COLUMNS[kind]} rows={list.items} rowKey={(r) => r.id} testid="logs-table" />
        <MoreFooter list={list} noun="ligne(s)" testid="logs" />
      </ListFrame>
    </div>
  );
}
